import { useMemo } from 'react';
import { Address, erc20Abi } from 'viem';
import { useAccount, useReadContracts } from 'wagmi';
import { useExchangeAddress } from './useDataForSigning';
import { TokenDetails, useTokenDetails } from './useTokenDetails';

export type TokenBalance = {
  address: Address;
  name: string;
  balanceNativeDec: bigint;
  allowanceNativeDec: bigint;
  decimals: number;
  symbol: string;
  isWrappedNative?: boolean;
};

type ContractCallConfig<T extends readonly unknown[] = readonly unknown[]> = {
  abi: typeof erc20Abi;
  address: Address;
  functionName: string;
  args?: T;
};

type ContractCallResult =
  | { error?: undefined; result: string | number | bigint; status: 'success' }
  | { error: Error; result?: undefined; status: 'failure' };

export function formatReadContractsData(
  supportedTokens: TokenDetails[] | undefined,
  results: ContractCallResult[]
): { tokenBalances: TokenBalance[]; errors: Error[] } {
  if (!supportedTokens) {
    // Query should not be enable if supportedTokens is undefined.
    throw new Error('supportedTokens is undefined');
  }
  const expectedResultsCount = supportedTokens.length * 2;
  if (results.length !== expectedResultsCount) {
    // This should never happen...
    throw new Error(`Results mismatch: expected ${expectedResultsCount}, got ${results.length}`);
  }

  return supportedTokens.reduce<{ tokenBalances: TokenBalance[]; errors: Error[] }>(
    (acc, token, index) => {
      const startIndex = index * 2;
      const slice = results.slice(startIndex, startIndex + 2);
      const [balanceResult, allowanceResult] = slice;

      if (
        typeof balanceResult?.result === 'bigint' &&
        typeof allowanceResult?.result === 'bigint' &&
        token.erc20Decimals &&
        token.erc20Symbol
      ) {
        acc.tokenBalances.push({
          address: token.address,
          name: token.name,
          balanceNativeDec: balanceResult.result,
          allowanceNativeDec: allowanceResult.result,
          decimals: token.erc20Decimals,
          symbol: token.erc20Symbol,
          isWrappedNative: token.erc20Symbol === 'WUSDe',
        });
      } else {
        const failedResults = slice.filter((result) => result.status === 'failure');
        failedResults.forEach((result) => {
          if (result.error) acc.errors.push(result.error);
        });
        console.error(`Invalid result for token at address ${token.address}:`, slice);
      }

      return acc;
    },
    { tokenBalances: [], errors: [] }
  );
}

export const useSupportedTokenBalances = () => {
  const { address: walletAddress } = useAccount();
  const { address: exchangeGatewayAddress } = useExchangeAddress();
  const { data: tokens } = useTokenDetails();
  const supportedTokens = useMemo(
    () => tokens?.filter((token) => token.address && token.depositEnabled),
    [tokens]
  );

  const contractCalls = useMemo<ContractCallConfig[]>(() => {
    if (!walletAddress || !supportedTokens) return [];

    return supportedTokens.flatMap((token): ContractCallConfig[] => [
      {
        abi: erc20Abi,
        address: token.address,
        functionName: 'balanceOf',
        args: [walletAddress],
      },
      {
        abi: erc20Abi,
        address: token.address,
        functionName: 'allowance',
        args: [walletAddress, exchangeGatewayAddress],
      },
    ]);
  }, [walletAddress, supportedTokens, exchangeGatewayAddress]);

  return useReadContracts({
    contracts: contractCalls,
    query: {
      enabled: Boolean(supportedTokens && walletAddress),
      select: (data) => {
        const { tokenBalances, errors } = formatReadContractsData(supportedTokens, data);
        if (errors.length > 0) {
          throw new AggregateError(errors, 'One or more contract calls failed');
        }
        return tokenBalances;
      },
    },
  });
};
